import React, { useState, useEffect, useCallback } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { getDRE, DREEntry } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import { getMonthRange } from '../hooks/dashboardUtils';

export default function DREPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<DREEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { end } = getMonthRange(year, month);
      const beginYear = year - 1;
      const begin = `${beginYear}-${String(month).padStart(2, '0')}-01`;
      const result = await getDRE({ begin, end });
      setData(result.months);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar DRE');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <LoadingSpinner message="Carregando DRE..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Demonstrativo de Resultado (DRE)
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bars" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
              <Legend />
              <Bar yAxisId="bars" dataKey="receitas" name="Receitas" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="bars" dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line yAxisId="line" type="monotone" dataKey="resultado_acumulado" name="Resultado Acumulado" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Detalhamento Mensal
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Mês</th>
                  <th className="text-right py-3 px-4 font-medium text-green-600">Receitas</th>
                  <th className="text-right py-3 px-4 font-medium text-red-600">Despesas</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Resultado</th>
                  <th className="text-right py-3 px-4 font-medium text-blue-600">Resultado Acumulado</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={`${row.year}-${row.month}`} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-100">
                    <td className="py-3 px-4 text-gray-700 font-medium">{row.label}</td>
                    <td className="py-3 px-4 text-right text-green-600 font-mono">{formatBRL(row.receitas)}</td>
                    <td className="py-3 px-4 text-right text-red-600 font-mono">{formatBRL(row.despesas)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${row.resultado >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      {formatBRL(row.resultado)}
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-medium ${row.resultado_acumulado >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      {formatBRL(row.resultado_acumulado)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
